import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "./appointment-card.css";

const textFromHtml = (value) => {
  if (!value) return "";
  return String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

export const mapAppointmentToCardModel = (appointment) => {
  const observation = appointment?.observations?.[0] || {};
  const payment = appointment?.payments?.[0] || {};
  const notes = [
    { label: "Examination", content: observation?.examination },
    { label: "Other notes", content: observation?.other_notes },
    { label: "Reminder", content: observation?.reminder_note },
    ...(appointment?.dictionary_notes || []).map((note) => ({
      label: "Dictionary note",
      content: note?.notes,
    })),
  ].filter((note) => Boolean(note.content));

  const chartsByLabel = new Map();
  (appointment?.indicators || []).forEach((indicator) => {
    const label = indicator?.name || indicator?.label || indicator?.property || "Indicator";
    if (!chartsByLabel.has(label)) {
      chartsByLabel.set(label, {
        label,
        unit: indicator?.unit || indicator?.unite || "",
        points: [],
      });
    }
    chartsByLabel.get(label).points.push({
      value: String(indicator?.value ?? indicator?.valeur ?? ""),
      date: moment(indicator?.measured_at || indicator?.created_at).format("D MMM YYYY"),
    });
  });

  return {
    id: appointment?.id,
    date: appointment?.appointment_date,
    time: appointment?.time || "",
    type: appointment?.consultation_type || "Consultation",
    title: observation?.medical_condition_title || appointment?.reason || "Consultation",
    status: appointment?.finish === 1 ? "Completed" : "Pending",
    payment: {
      paid: payment?.amount ?? 0,
      due: payment?.rest_topay ?? 0,
      currency: "€",
    },
    details: {
      diagnosis: observation?.medical_condition_title || "No diagnosis recorded",
      summary: textFromHtml(observation?.other_notes) || textFromHtml(appointment?.dictionary_notes?.[0]?.notes) || "No clinical note recorded",
      treatment: textFromHtml(observation?.examination) || "No treatment plan recorded",
    },
    sections: {
      notes,
      careProtocol: appointment?.care_protocol || appointment?.careProtocol || [],
      charts: Array.from(chartsByLabel.values()),
      prescriptions: (appointment?.prescription_content || []).map((prescription, index) => ({
        id: prescription?.id || `${appointment?.id}-prescription-${index}`,
        title: prescription?.template_name || prescription?.name || "Prescription",
        html: prescription?.content || prescription?.body_html || prescription?.notes || "",
      })),
      documents: appointment?.documents || [],
    },
  };
};

export const AppointmentSection = ({
  children,
  count = 0,
  loadDelay = 700,
  name,
  onLoad,
  queryKey,
  title,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const timerRef = useRef(null);

  const sectionQuery = useQuery({
    queryKey: queryKey || ["patient-dictionary", "appointment-sections", "disabled", name],
    queryFn: onLoad || (() => Promise.resolve(null)),
    enabled: Boolean(onLoad) && isOpen,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
    meta: { suppressGlobalToast: true },
  });

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const toggleSection = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);

    if (willOpen && !hasLoaded) {
      if (!onLoad) {
        setIsLoading(true);
        timerRef.current = setTimeout(() => {
          setIsLoading(false);
          setHasLoaded(true);
        }, loadDelay);
      }
    }
  };

  return (
    <section className={`appointmentInnerSection ${isOpen ? "isOpen" : ""}`}>
      <button
        type="button"
        className="appointmentInnerSectionToggle"
        aria-expanded={isOpen}
        aria-label={`${isOpen ? "Close" : "Open"} ${title}`}
        onClick={toggleSection}
      >
        <span>{title}</span>
        <span className="appointmentSectionCount">{count}</span>
        <span aria-hidden="true">⌄</span>
      </button>
      {isOpen ? (
        <div className="appointmentInnerSectionBody">
          {(onLoad ? sectionQuery.isFetching && sectionQuery.data === undefined : isLoading) ? (
            <div data-testid={`${name}-section-skeleton`}>
              <Skeleton height={14} count={2} baseColor="#e9ecf7" highlightColor="#f7f8fc" />
            </div>
          ) : children}
        </div>
      ) : null}
    </section>
  );
};

export const AppointmentCard = memo(function AppointmentCard({
  appointment,
  loadDelay = 700,
  loadSection,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [sections, setSections] = useState(appointment.sections || {});
  const loadingTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(loadingTimerRef.current), []);

  const toggleDetails = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);

    if (willOpen && !hasLoaded) {
      setIsLoading(true);
      loadingTimerRef.current = setTimeout(() => {
        setIsLoading(false);
        setHasLoaded(true);
      }, loadDelay);
    }
  };

  const loadAppointmentSection = useCallback(async (sectionName) => {
    if (!loadSection) return;
    const sectionItems = await loadSection(appointment.id, sectionName);
    if (Array.isArray(sectionItems)) {
      setSections((currentSections) => ({
        ...currentSections,
        [sectionName]: sectionItems,
      }));
    }
    return sectionItems;
  }, [appointment.id, loadSection]);

  const sectionQueryKey = useCallback(
    (sectionName) => [
      "patient-dictionary",
      "appointment-sections",
      String(appointment.id),
      sectionName,
    ],
    [appointment.id],
  );

  return (
    <article className={`improvedAppointmentCard ${isOpen ? "isOpen" : ""}`}>
      <button
        type="button"
        className="improvedAppointmentCardHeader"
        aria-expanded={isOpen}
        aria-label={`${isOpen ? "Close" : "Open"} appointment ${appointment.title}`}
        onClick={toggleDetails}
      >
        <span className="improvedAppointmentDate">
          <strong>{moment(appointment.date).format("DD")}</strong>
          <span>{moment(appointment.date).format("MMM YYYY")}</span>
        </span>
        <span className="improvedAppointmentHeading">
          <strong>{appointment.title}</strong>
          <small>{appointment.time} · {appointment.type}</small>
        </span>
        <span className={`improvedAppointmentStatus status${appointment.status}`}>
          {appointment.status}
        </span>
        <span className="improvedAppointmentPayment">
          <strong>Paid: {appointment.payment?.currency}{appointment.payment?.paid ?? 0}</strong>
          <small>Due: {appointment.payment?.currency}{appointment.payment?.due ?? 0}</small>
        </span>
        <span className="improvedAppointmentChevron" aria-hidden="true">⌄</span>
      </button>

      <div className="improvedAppointmentVisibleSummary">
        <div><small>Diagnosis</small><p>{appointment.details.diagnosis}</p></div>
        <div><small>Clinical note</small><p>{appointment.details.summary}</p></div>
      </div>

      {isOpen ? (
        <div className="improvedAppointmentDetails">
          {isLoading ? (
            <div data-testid="appointment-card-skeleton" aria-label="Loading appointment details">
              <Skeleton height={16} count={3} baseColor="#e9ecf7" highlightColor="#f7f8fc" />
            </div>
          ) : (
            <>
              <div className="improvedAppointmentTreatmentSummary">
                <small>Treatment plan</small>
                <p>{appointment.details.treatment}</p>
              </div>
              <div className="appointmentInnerSections">
                <AppointmentSection name="notes" title="Clinical notes" count={sections.notes?.length} loadDelay={loadDelay} queryKey={sectionQueryKey("notes")} onLoad={loadSection ? () => loadAppointmentSection("notes") : undefined}>
                  <div className="appointmentNotesList">
                    {sections.notes?.map((note) => (
                      <div key={note.id || note.label}>
                        <small>{note.label}</small>
                        <div
                          className="appointmentNoteHtml"
                          dangerouslySetInnerHTML={{ __html: note.content || "" }}
                        />
                      </div>
                    ))}
                  </div>
                </AppointmentSection>
                <AppointmentSection name="care-protocol" title="Care protocol" count={sections.careProtocol?.length} loadDelay={loadDelay} queryKey={sectionQueryKey("careProtocol")} onLoad={loadSection ? () => loadAppointmentSection("careProtocol") : undefined}>
                  <div className="appointmentProtocolList">
                    {sections.careProtocol?.map((step) => <span className={step.done ? "isDone" : ""} key={step.id || step.label}>{step.done ? "✓" : "○"} {step.label}</span>)}
                  </div>
                </AppointmentSection>
                <AppointmentSection name="charts" title="Charts" count={sections.charts?.length} loadDelay={loadDelay} queryKey={sectionQueryKey("charts")} onLoad={loadSection ? () => loadAppointmentSection("charts") : undefined}>
                  <div className="appointmentChartList">
                    {sections.charts?.map((chart) => (
                      <div className="appointmentChartGroup" key={chart.label}>
                        <div className="appointmentChartGroupHeader"><strong>{chart.label}</strong><small>{chart.unit || ""}</small></div>
                        <div className="appointmentChartPoints">
                          {chart.points?.map((point, index) => <div className="appointmentChartPoint" key={`${point.date}-${index}`}><span>{point.value}{chart.unit ? ` ${chart.unit}` : ""}</span><small>{point.date}</small></div>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </AppointmentSection>
                <AppointmentSection name="prescriptions" title="Prescriptions" count={sections.prescriptions?.length} loadDelay={loadDelay} queryKey={sectionQueryKey("prescriptions")} onLoad={loadSection ? () => loadAppointmentSection("prescriptions") : undefined}>
                  <div className="appointmentPrescriptionButtons">
                    {sections.prescriptions?.map((prescription) => <button type="button" key={prescription.id} onClick={() => setSelectedPrescription(prescription)}>{prescription.title}</button>)}
                  </div>
                </AppointmentSection>
                <AppointmentSection name="documents" title="Files & documents" count={sections.documents?.length} loadDelay={loadDelay} queryKey={sectionQueryKey("documents")} onLoad={loadSection ? () => loadAppointmentSection("documents") : undefined}>
                  <div className="appointmentDocumentList">
                    {sections.documents?.map((document) => <button type="button" key={document.id || document.name}><span>{document.type}</span><strong>{document.name}</strong><small>{document.date}</small></button>)}
                  </div>
                </AppointmentSection>
              </div>
            </>
          )}
        </div>
      ) : null}

      {selectedPrescription ? (
        <div className="appointmentPrescriptionModal" role="dialog" aria-modal="true" aria-label={selectedPrescription.title}>
          <div className="appointmentPrescriptionModalContent">
            <div className="appointmentPrescriptionModalHeader">
              <h3>{selectedPrescription.title}</h3>
              <button type="button" aria-label="Close prescription" onClick={() => setSelectedPrescription(null)}>×</button>
            </div>
            <div className="appointmentPrescriptionHtml" dangerouslySetInnerHTML={{ __html: selectedPrescription.html }} />
          </div>
        </div>
      ) : null}
    </article>
  );
});

export const AppointmentList = ({
  endpoint = "",
  initialItems = [],
  loadPage,
  loadSection,
  size = 10,
}) => {
  const queryClient = useQueryClient();
  const [appointments, setAppointments] = useState(() => initialItems.slice(0, size));
  const [page, setPage] = useState(() => (initialItems.length ? 1 : 0));
  const [hasMore, setHasMore] = useState(
    () => initialItems.length > size || Boolean(loadPage),
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadNextPage = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    const nextPage = page + 1;

    if (!loadPage) {
      const nextItems = initialItems.slice((nextPage - 1) * size, nextPage * size);
      setAppointments((currentItems) => [...currentItems, ...nextItems]);
      setPage(nextPage);
      setHasMore(nextPage * size < initialItems.length);
      return;
    }

    const separator = endpoint.includes("?") ? "&" : "?";
    const url = `${endpoint}${separator}page=${nextPage}&size=${size}`;

    setIsLoadingMore(true);
    setLoadError("");

    try {
      const result = await loadPage(url, { page: nextPage, size });
      const incomingItems = Array.isArray(result?.items) ? result.items : [];

      setAppointments((currentItems) => {
        const appointmentsById = new Map(
          currentItems.map((appointment) => [appointment.id, appointment]),
        );
        incomingItems.forEach((appointment) => {
          appointmentsById.set(appointment.id, appointment);
        });
        return Array.from(appointmentsById.values());
      });
      setPage(nextPage);
      setHasMore(Boolean(result?.hasMore));
    } catch {
      setLoadError("Unable to load more appointments.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [endpoint, hasMore, initialItems, isLoadingMore, loadPage, page, size]);

  useEffect(() => {
    if (!initialItems.length) return;
    setAppointments(initialItems.slice(0, size));
    setPage(1);
    setHasMore(initialItems.length > size || Boolean(loadPage));
  }, [initialItems, loadPage, size]);

  const initialLoadRef = useRef(false);
  useEffect(() => {
    if (initialLoadRef.current || initialItems.length || !loadPage) return;
    initialLoadRef.current = true;
    loadNextPage();
  }, [initialItems.length, loadNextPage, loadPage]);

  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: ["patient-dictionary", "appointment-sections"],
      });
    },
    [queryClient],
  );

  return (
    <div className="improvedAppointmentList">
      {appointments.map((appointment) => (
        <AppointmentCard
          key={appointment.id}
          appointment={appointment}
          loadSection={loadSection}
        />
      ))}

      {isLoadingMore ? (
        <div className="appointmentListLoading" aria-label="Loading appointments">
          <Skeleton height={94} count={2} baseColor="#e9ecf7" highlightColor="#f7f8fc" />
        </div>
      ) : null}

      {loadError ? <p className="appointmentListError" role="alert">{loadError}</p> : null}

      {hasMore && !isLoadingMore ? (
        <button type="button" className="appointmentLoadMoreButton" onClick={loadNextPage}>
          Load more appointments
        </button>
      ) : null}
    </div>
  );
};

export default AppointmentCard;
